import { useMemo } from 'react'

import { useAvailabilityConfiguration } from '../AvailabilityConfiguration/hooks/useAvailabilityConfiguration'
import { findMismatches } from '../AvailabilityConfiguration/utils/availability.utils'
import { useBeachPlans } from '../BeachPlanConfiguration/hooks/useBeachPlans'
import { usePricingConfiguration } from '../PricingConfiguration/hooks/usePricingConfiguration'
import { findIncompletePricingConfigurations } from '../PricingConfiguration/utils/pricing.utils'
import { useFutureSucceededBookingsForWarnings } from './useFutureSucceededBookingsForWarnings'
import { findInvalidFutureConfigurationBookings } from '../utils/invalidConfigurationBookings.utils'

export const useConfigurationWarningsForMenu = (enabled: boolean) => {
  const { openDates, closeDates } = useAvailabilityConfiguration(enabled)
  const { lists } = usePricingConfiguration(enabled)
  const { beachPlans } = useBeachPlans(enabled)
  const spots = beachPlans.flatMap((plan) => plan.spots || [])
  const hasConfiguredBeachPlan = beachPlans.some((plan) => (plan.spots?.length ?? 0) > 0 && plan.rows > 0 && plan.columns > 0)
  const { bookings: futureSucceededBookings } = useFutureSucceededBookingsForWarnings(enabled)

  return useMemo(() => {
    if (!enabled) {
      return {
        pricingHasWarnings: false,
        availabilityHasWarnings: false,
      }
    }

    const { openDaysWithNoPriceLists, priceListsWithNoOpenDays } = findMismatches(openDates, lists)
    const incompletePricingConfigurations = findIncompletePricingConfigurations(lists, spots)
    const hasNoBeachPlanConfiguration = !hasConfiguredBeachPlan
    const hasNoAvailabilityConfiguration = openDates.length === 0
    const hasPricingMismatches = openDaysWithNoPriceLists.length > 0 || priceListsWithNoOpenDays.length > 0
    const hasIncompletePricingConfiguration = incompletePricingConfigurations.length > 0

    const invalidBookings = findInvalidFutureConfigurationBookings(futureSucceededBookings, openDates, closeDates)

    return {
      pricingHasWarnings: hasNoAvailabilityConfiguration || hasNoBeachPlanConfiguration || hasPricingMismatches || hasIncompletePricingConfiguration,
      availabilityHasWarnings: hasPricingMismatches || invalidBookings.length > 0,
    }
  }, [enabled, openDates, lists, spots, hasConfiguredBeachPlan, futureSucceededBookings, closeDates])
}
